import React, { createContext, useCallback, useContext, useState } from 'react'
import axios from '../config/axios'

export interface MentionNotification {
  id: number
  viewed: boolean
  createdAt: string
  Comment: {
    id: number
    comment: string
    User: { id: number; name: string; picture?: string } | null
    Ticket: {
      id: number
      title: string
      Step: { pipelineId: number }
    } | null
  }
}

interface NotificationContextValue {
  mentions: MentionNotification[]
  unreadCount: number
  refresh: () => Promise<void>
  markRead: (ids?: number[]) => Promise<void>
}

const NotificationContext = createContext<NotificationContextValue>({
  mentions: [],
  unreadCount: 0,
  refresh: async () => {},
  markRead: async () => {},
})

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mentions, setMentions] = useState<MentionNotification[]>([])

  const refresh = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/comments/mentions`
      )
      if (Array.isArray(response.data)) {
        setMentions(response.data)
      }
    } catch {
      // silencioso — usuário pode não ter acesso ao módulo de pipelines
    }
  }, [])

  const markRead = useCallback(async (ids?: number[]) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/comments/mentions/read`,
        { ids }
      )
      setMentions((prev) =>
        prev.map((m) =>
          !ids || ids.includes(m.id) ? { ...m, viewed: true } : m
        )
      )
    } catch {
      // silencioso
    }
  }, [])

  const unreadCount = mentions.filter((m) => !m.viewed).length

  return (
    <NotificationContext.Provider value={{ mentions, unreadCount, refresh, markRead }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => useContext(NotificationContext)

export default NotificationContext
